// frontend/src/App.tsx
// Complete Updated Version with Landing Page
// Updated: Removed Location page, streamlined navigation

import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Container,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Folder as FolderIcon,
  Map as MapIcon,
  TrendingUp as TrendingUpIcon, 
  Notifications as MonitorIcon,
  AccountCircle,
  ExitToApp,
  Description as MethodologyIcon,
  Groups as GroupsIcon,
  AdminPanelSettings as AdminIcon,
  Lock as LockIcon,
  AttachMoney as FinancialIcon,
} from '@mui/icons-material';
import 'mapbox-gl/dist/mapbox-gl.css';
import axios from 'axios';

// Import theme
import modernTheme from './theme/theme';

// Page imports - files are in src/pages/
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import Register from './pages/Register';
import Portfolio from './pages/Portfolio';
import People from './pages/People';
import ClimateRiskMap from './pages/Map';
import Monitor from './pages/Monitor';
import FinancialImpactDashboardPage from './pages/FinancialImpactDashboardPage';
import LocationRiskIndex from './pages/LocationRiskIndex';
import Methodology from './pages/Methodology';
import AdminDashboard from './pages/AdminDashboard';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import LandingPage from './pages/LandingPage';


const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://spatial-risk-api.onrender.com/api';

// ============================================
// PROTECTED ROUTE COMPONENT WITH PAGE ACCESS
// ============================================
interface ProtectedRouteProps {
  children: React.ReactNode;
  pageName?: string;
  pageDisplayName?: string;
}

function ProtectedRoute({ children, pageName, pageDisplayName }: ProtectedRouteProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkAccess = async () => {
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');

      if (!token || !userStr) {
        navigate('/login');
        return;
      }

      try {
        const user = JSON.parse(userStr);
        
        // Check if this is a dev token (mock token for local development)
        const isDevToken = token.startsWith('dev-token-');
        
        if (isDevToken) {
          console.log('🔧 Dev mode: Bypassing API verification');
          
          // For dev tokens, use localStorage data directly
          // If no specific page access check needed
          if (!pageName) {
            setHasAccess(true);
            setLoading(false);
            return;
          }

          // Admin has access to everything
          if (user.role === 'admin') {
            console.log('✅ Dev admin access granted to:', pageName);
            setHasAccess(true);
            setLoading(false);
            return;
          }

          // For regular dev users, check if page is in their pages array
          const userPages = user.pages || [];
          if (userPages.includes(pageName)) {
            console.log('✅ Dev user access granted to:', pageName);
            setHasAccess(true);
          } else {
            setHasAccess(false);
            setError(`You don't have access to ${pageDisplayName || 'this page'}`);
          }
          setLoading(false);
          return;
        }
        
        // For real tokens, proceed with normal verification
        // If no specific page access check needed
        if (!pageName) {
          setHasAccess(true);
          setLoading(false);
          return;
        }

        // Admin has access to everything
        if (user.role === 'admin') {
          setHasAccess(true);
          setLoading(false);
          return;
        }

        // For regular users, verify with API
        const response = await axios.get(`${API_BASE_URL}/auth/verify`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const userData = response.data.user;
        const userPages = userData.pages || [];

        // Check if user has access to this specific page
        if (userPages.includes(pageName)) {
          setHasAccess(true);
        } else {
          setHasAccess(false);
          setError(`You don't have access to ${pageDisplayName || 'this page'}`);
        }
      } catch (err: any) {
        console.error('Access check error:', err);
        
        // Check if this might be a dev token that we should handle gracefully
        const token = localStorage.getItem('token');
        const isDevToken = token?.startsWith('dev-token-');
        
        if (isDevToken) {
          // For dev tokens that fail API call, fall back to localStorage
          console.log('⚠️ Dev mode: API call failed, using localStorage fallback');
          const userStr = localStorage.getItem('user');
          if (userStr) {
            const user = JSON.parse(userStr);
            
            if (!pageName || user.role === 'admin') {
              setHasAccess(true);
              setLoading(false);
              return;
            }
            
            const userPages = user.pages || [];
            if (userPages.includes(pageName)) {
              setHasAccess(true);
            } else {
              setHasAccess(false);
              setError(`You don't have access to ${pageDisplayName || 'this page'}`);
            }
            setLoading(false);
            return;
          }
        }
        
        // For real authentication errors
        if (err.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          navigate('/login');
        } else {
          setError('Failed to verify access permissions');
        }
      } finally {
        setLoading(false);
      }
    };

    checkAccess();
  }, [navigate, pageName, pageDisplayName]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: '#F9FAFB',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!hasAccess && error) {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <LockIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Access Restricted
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            {error}
          </Typography>
          <Alert severity="info" sx={{ mt: 2, mb: 3 }}>
            Contact your administrator to request access to this page.
          </Alert>
          <Button
            variant="contained"
            onClick={() => navigate('/dashboard')}
          >
            Go to Dashboard
          </Button>
        </Paper>
      </Container>
    );
  }

  return <>{children}</>;
}

// ============================================
// NAVIGATION COMPONENT
// ============================================
function Navigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [userPages, setUserPages] = useState<string[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchUserAccess();
  }, []);

  const fetchUserAccess = async () => {
    try {
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');
      
      if (!token || !userStr) return;
      
      const user = JSON.parse(userStr);
      
      // Check if admin
      if (user.role === 'admin') {
        setIsAdmin(true);
        return;
      }
      
      // Check if dev token
      const isDevToken = token.startsWith('dev-token-');
      
      if (isDevToken) {
        // For dev tokens, use localStorage data
        setUserPages(user.pages || []);
        return;
      }
      
      // For real tokens, verify with API
      const response = await axios.get(`${API_BASE_URL}/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setUserPages(response.data.user.pages || []);
    } catch (err) {
      console.error('Failed to fetch user access:', err);
      // Fallback to localStorage
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        setUserPages(user.pages || []);
      }
    }
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const isActive = (path: string) => location.pathname === path;

// Navigation items with page names for access control
const allNavItems = [
  { path: '/dashboard', label: 'Dashboard', icon: <DashboardIcon />, pageName: 'dashboard' },
  { path: '/portfolio', label: 'Portfolio', icon: <FolderIcon />, pageName: 'portfolio' },
  { path: '/people', label: 'People', icon: <GroupsIcon />, pageName: 'people' },
  { path: '/map', label: 'Map', icon: <MapIcon />, pageName: 'map' },
  { path: '/risk-index', label: 'Risk Index', icon: <TrendingUpIcon />, pageName: 'risk-index' },
  { path: '/monitor', label: 'Monitor', icon: <MonitorIcon />, pageName: 'monitor' },
  { path: '/methodology', label: 'Methodology', icon: <MethodologyIcon />, pageName: 'methodology' },
];

  // Filter nav items based on user access
  const navItems = isAdmin 
    ? allNavItems 
    : allNavItems.filter(item => userPages.includes(item.pageName));

  return (
    <AppBar 
      position="sticky" 
      sx={{ 
        background: 'linear-gradient(135deg, #151935 0%, #0A0E27 100%)',
        borderBottom: '1px solid rgba(0, 212, 255, 0.2)',
      }}
    >
      <Toolbar>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            cursor: 'pointer',
            mr: 4,
          }}
          onClick={() => navigate('/dashboard')}
        >
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 700,
              background: 'linear-gradient(135deg, #00D4FF 0%, #00A8CC 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: '1.5rem',
            }}
          >
            Spatial Risk Q
          </Typography>
        </Box>

        <Box sx={{ flexGrow: 1, display: 'flex', gap: 1 }}>
          {navItems.map((item) => (
            <Button
              key={item.path}
              onClick={() => navigate(item.path)}
              startIcon={item.icon}
              sx={{
                color: isActive(item.path) ? '#00D4FF' : '#FFFFFF',
                backgroundColor: isActive(item.path) 
                  ? 'rgba(0, 212, 255, 0.1)' 
                  : 'transparent',
                fontWeight: isActive(item.path) ? 700 : 500,
                px: 2,
                py: 1,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                '&:hover': {
                  backgroundColor: 'rgba(0, 212, 255, 0.15)',
                },
              }}
            >
              {item.label}
            </Button>
          ))}
          
          {/* Admin Button */}
          {isAdmin && (
            <Button
              onClick={() => navigate('/admin')}
              startIcon={<AdminIcon />}
              sx={{
                color: isActive('/admin') ? '#00D4FF' : '#FFFFFF',
                backgroundColor: isActive('/admin') 
                  ? 'rgba(0, 212, 255, 0.1)' 
                  : 'transparent',
                fontWeight: isActive('/admin') ? 700 : 500,
                px: 2,
                py: 1,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                '&:hover': {
                  backgroundColor: 'rgba(0, 212, 255, 0.15)',
                },
              }}
            >
              Admin
            </Button>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
            {user.name || user.email}
          </Typography>
          <IconButton onClick={handleMenu} sx={{ color: '#00D4FF' }}>
            <AccountCircle />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <MenuItem onClick={handleLogout}>
              <ExitToApp sx={{ mr: 1 }} />
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}
// ============================================
// APP LAYOUT WITH NAVIGATION
// ============================================
function AppLayout({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Navigation />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: '#F9FAFB',
          overflow: 'auto',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

// ============================================
// MAIN APP COMPONENT
// ============================================
function App() {
  return (
    <ThemeProvider theme={modernTheme}>
      <CssBaseline />
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          {/* Remove register route - using contact form instead */}
          {/* <Route path="/register" element={<Register />} /> */}

          {/* Protected Routes with Page-Level Access Control */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute pageName="dashboard" pageDisplayName="Dashboard">
                <AppLayout>
                  <Dashboard />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/portfolio"
            element={
              <ProtectedRoute pageName="portfolio" pageDisplayName="Portfolio Management">
                <AppLayout>
                  <Portfolio />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/people"
            element={
              <ProtectedRoute pageName="people" pageDisplayName="People Metrics">
                <AppLayout>
                  <People />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/map"
            element={
              <ProtectedRoute pageName="map" pageDisplayName="Interactive Map">
                <AppLayout>
                  <ClimateRiskMap />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
  path="/risk-index"
  element={
    <ProtectedRoute pageName="risk-index" pageDisplayName="Risk Index">
      <AppLayout>
        <LocationRiskIndex />
      </AppLayout>
    </ProtectedRoute>
  }
/>
          
          <Route
            path="/monitor"
            element={
              <ProtectedRoute pageName="monitor" pageDisplayName="Supply Chain Monitor">
                <AppLayout>
                  <Monitor />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/financial-impact"
            element={
              <ProtectedRoute pageName="dashboard" pageDisplayName="Financial Impact">
                <AppLayout>
                  <FinancialImpactDashboardPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          
          <Route
            path="/methodology"
            element={
              <ProtectedRoute pageName="methodology" pageDisplayName="Methodology">
                <AppLayout>
                  <Methodology />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Admin Dashboard - Accessible Only to Admins */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <AdminDashboard />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Redirect /location to /dashboard for backwards compatibility */}
          <Route path="/location" element={<Navigate to="/dashboard" replace />} />

          {/* Catch all - redirect to landing page */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;